import styled from 'styled-components';
import { Link } from 'ui/links';
import {
  backgroundColors,
  body2,
  duration,
  easing,
  heading3,
  heading5,
  spacing,
  subtleShadow,
  zIndex,
} from 'ui/theme';
import { PageWidthStyle } from 'ui/theme/spacing';
import { PAGE_HEADER_VARIANTS } from './constants';

export const HeaderContent = styled.div<{ isPastScrollBreakpoint?: boolean }>`
  &[data-variant=${PAGE_HEADER_VARIANTS.PRIMARY}],
  &[data-variant=${PAGE_HEADER_VARIANTS.SECONDARY}],
  &[data-variant=${PAGE_HEADER_VARIANTS.TERTIARY}] {
    ${heading3}
  }
  &[data-variant=${PAGE_HEADER_VARIANTS.STICKY}] {
    ${({ isPastScrollBreakpoint }) => (isPastScrollBreakpoint ? heading5 : heading3)}
    transition: all ${duration.short} ${easing.standard};
  }
  &[data-variant=${PAGE_HEADER_VARIANTS.SECONDARY}] {
    margin-top: ${spacing.base};
  }
`;

export const DocLink = styled(Link)`
  margin-bottom: ${spacing.plusHalf};

  @media only screen and (max-width: ${spacing.x48}) {
    margin-bottom: ${spacing.base};
  }
`;

// for PAGE_HEADER_VARIANTS.SECONDARY, setting widthStyle === PageWidthStyle.normal limits the width
// of the header while allowing the background color to extend to the edges of the viewport
export const PageHeaderContainer = styled.div<{
  widthStyle?: PageWidthStyle;
  navBarHeight?: number;
  isPastScrollBreakpoint?: boolean;
}>`
  margin: ${spacing.base} 0;
  &[data-variant=${PAGE_HEADER_VARIANTS.SECONDARY}] {
    background-color: ${backgroundColors.backgroundSecondary};
    ${({ widthStyle }) =>
      widthStyle === PageWidthStyle.normal
        ? `
        margin: -${spacing.layoutTopPadding} min(calc((100vw - ${spacing.normalPageMaxWidth})/-2), -${spacing.layoutLRPadding}) 0 min(calc((100vw - ${spacing.normalPageMaxWidth})/-2), -${spacing.layoutLRPadding});
        padding: ${spacing.plusHalf} max(calc((100vw - ${spacing.normalPageMaxWidth})/2), ${spacing.layoutLRPadding}) ${spacing.layoutLRPadding};
      `
        : `
        margin: -${spacing.layoutTopPadding} -${spacing.layoutLRPadding} 0 -${spacing.layoutLRPadding};
        padding: ${spacing.plusHalf} ${spacing.layoutLRPadding} ${spacing.layoutLRPadding};
      `}
  }

  /** common styles between sticky and tertiary */
  &[data-variant=${PAGE_HEADER_VARIANTS.STICKY}],
  &[data-variant=${PAGE_HEADER_VARIANTS.TERTIARY}] {
    ${subtleShadow};
    position: relative;
    z-index: ${zIndex.dropdown};
    padding: ${spacing.plusHalf} ${spacing.layoutLRPadding};
    margin: 0 -${spacing.layoutLRPadding};
    background-color: ${backgroundColors.backgroundPrimary};
    transition: padding ${duration.short} ${easing.standard};
  }
  
  &[data-variant=${PAGE_HEADER_VARIANTS.STICKY}] {
    position: fixed;
    width: 100%;
    top: ${({ navBarHeight }) => (navBarHeight ? navBarHeight - 1 + 20 : 0)}px;
    margin: -${spacing.layoutTopPadding} -${spacing.layoutLRPadding} 0 -${spacing.layoutLRPadding};
  }

  /* isSticky differs from STICKY variant by _position_ property */
  &[data-is-sticky="true"] {
    position: sticky;
    /* top position similar to sticky but removes the 20px offset from content padding to account for Announcements */
    top: ${({ navBarHeight }) => (navBarHeight ? navBarHeight - 1 : 0)}px;
  }

  /* result of _isPastScrollBreakpoint_ prop */
  &[data-has-scrolled="true"] {
    padding-top: ${spacing.half};
    padding-bottom: ${spacing.half};
  }
`;

export const PageHeaderContent = styled.div`
  display: flex;
  gap: ${spacing.base};
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  margin-right: auto;
  margin-left: auto;
  width: 100%;

  &&[data-use-page-max-width="true"] {
    max-width: ${spacing.normalPageMaxWidth};
  }
`;

export const HeaderColLeft = styled.div`
  flex: 1 1 auto;
  word-break: break-word;
`;

export const HeaderColRight = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  @media only screen and (max-width: ${spacing.x48}) {
    padding-top: ${spacing.half};
    align-items: flex-start;
  }
  &[data-variant=${PAGE_HEADER_VARIANTS.STICKY}] {
    @media only screen and (max-width: ${spacing.x48}) {
      padding-top: 0;
      align-items: flex-start;
    }
  }
`;

export const PageSubSectionWrapper = styled.div<{
  isPastScrollBreakpoint?: boolean;
}>`
  &[data-variant=${PAGE_HEADER_VARIANTS.SECONDARY}] {
    margin-top: ${spacing.half};
    ${body2}
  }

  &[data-variant=${PAGE_HEADER_VARIANTS.STICKY}],
  &[data-variant=${PAGE_HEADER_VARIANTS.TERTIARY}] {
    margin-top: ${spacing.threeQuarter};
    transition: margin-top ${duration.short} ${easing.standard};
    ${({ isPastScrollBreakpoint }) => (isPastScrollBreakpoint ? `margin-top: 0;` : ``)}
  }
`;

export const PageActionsContainer = styled.div`
  display: flex;
  gap: ${spacing.half};
`;

export const BreadcrumbsContainer = styled.div`
  display: inline-flex;
  justify-content: center;
`;
